import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { FileText, UploadSimple, Trash, FolderSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "passport", label: "Passport" },
  { key: "company-docs", label: "Company Docs" },
  { key: "banking-docs", label: "Banking Docs" },
  { key: "agreements", label: "Agreements" },
  { key: "other", label: "Other Files" },
];

export default function DocumentBrowser({ projectId }) {
  const [data, setData] = useState({ categories: {}, total: 0 });
  const [active, setActive] = useState("passport");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const refresh = async () => {
    const { data } = await api.get(`/portal/projects/${projectId}/documents`);
    setData(data);
  };
  useEffect(() => { refresh(); }, [projectId]);

  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    upload(file);
  };

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", active);
      await api.post(`/portal/projects/${projectId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} uploaded`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/portal/documents/${id}`);
      refresh();
    } catch (e) { toast.error("Delete failed"); }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/portal/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error("Download failed"); }
  };

  const items = data.categories?.[active] || [];

  return (
    <div data-testid="document-browser" className="bg-white border border-gos-border rounded-sm">
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr]">
        <aside className="border-r border-gos-border bg-gos-subtle">
          <div className="overline px-4 pt-4 mb-2">Folders</div>
          {CATEGORIES.map((c) => {
            const count = (data.categories?.[c.key] || []).length;
            return (
              <button
                key={c.key}
                onClick={() => setActive(c.key)}
                data-testid={`doc-folder-${c.key}`}
                className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left ${active === c.key ? "bg-white text-gos-navy border-l-2 border-gos-sand font-medium" : "text-gos-slate hover:bg-white/60"}`}
              >
                <FolderSimple size={16} weight={active === c.key ? "duotone" : "regular"} /> {c.label}
                <span className="ml-auto text-xs text-gos-muted font-mono">{count}</span>
              </button>
            );
          })}
        </aside>

        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">{CATEGORIES.find((c) => c.key === active)?.label} · {items.length} file(s)</div>
            <button data-testid="doc-upload-btn" onClick={() => inputRef.current?.click()} disabled={uploading}
              className="btn-primary text-xs !py-2 !px-3">
              <UploadSimple size={14} weight="bold" /> {uploading ? "Uploading…" : "Upload"}
            </button>
            <input ref={inputRef} type="file" hidden onChange={onPick} data-testid="doc-file-input" />
          </div>

          {items.length === 0 ? (
            <div className="text-sm text-gos-muted py-8 text-center border border-dashed border-gos-border rounded-sm">
              No files in this folder yet. Click <em>Upload</em> to add one (max 25 MB).
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((d) => (
                <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-3 border border-gos-border p-3 rounded-sm">
                  <FileText size={18} className="text-gos-navy" />
                  <button onClick={() => download(d)} className="flex-1 text-left text-sm text-gos-charcoal hover:text-gos-navy truncate" data-testid={`doc-download-${d.id}`}>
                    {d.original_filename}
                  </button>
                  <span className="text-xs text-gos-muted font-mono">{Math.round(d.size / 1024)} KB</span>
                  <button onClick={() => remove(d.id)} data-testid={`doc-delete-${d.id}`} className="text-gos-error hover:text-gos-navyHover"><Trash size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
